import { useGate, useUnit } from 'effector-react';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';
import { AutocompleteOptionWithHint } from '@shared/ui';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { useAutoSelectSingleOption } from '../../lib/use-auto-select-single-option';
import { ResidentAccountStatusControlGate, residentAccountStatusQuery } from './model';
import type { ResidentAccountStatus, Props } from './types';

export const ResidentAccountStatusControl = withControl<
  Partial<AutocompleteProps<ResidentAccountStatus, boolean, boolean, boolean>> & Props
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    clearable,
    placeholder,
    accountId,
    autoSelectSingleOption = false,
    ...props
  }) => {
    useGate(ResidentAccountStatusControlGate, { accountId });
    const { data, pending } = useUnit(residentAccountStatusQuery);

    const options = Array.isArray(data?.options) ? data.options : [];

    const setValueFromControl = useAutoSelectSingleOption({
      enabled: autoSelectSingleOption && Boolean(accountId),
      loading: pending,
      multiple,
      options,
      setValue,
      value,
    });

    const handleChange = (
      _: unknown,
      newValue: string | ResidentAccountStatus | (string | ResidentAccountStatus)[] | null
    ) => getAutocompleteOnChange(newValue, setValueFromControl, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText || data?.helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          loading={pending}
          disableClearable={!clearable}
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          renderOption={({ key, ...props }, option) => (
            <li key={key} {...props}>
              <AutocompleteOptionWithHint label={option.label} hint={option.message} />
            </li>
          )}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
